import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { useOrg } from 'contexts/OrgContext'

import { ALL_ENTITIES_VALUE, useOrgLegalEntities } from './useOrgLegalEntities'

export const orgManagerSchema = z.object({
  orgId: z.string(),
  legalEntityId: z.string().optional(),
})

export type OrgManagerFormValues = z.infer<typeof orgManagerSchema>

const useOrgManagerFormMethods = () => {
  const { org: currentOrg } = useOrg()
  const { findParentOrg, isLoading, hasEntities } = useOrgLegalEntities()

  const methods = useForm<OrgManagerFormValues>({
    resolver: zodResolver(orgManagerSchema),
    defaultValues: {
      orgId: '',
      legalEntityId: undefined,
    },
  })

  // Update form values when data loads and we have a current org
  useEffect(() => {
    if (!isLoading && currentOrg) {
      const parentOrgId = findParentOrg(currentOrg)

      if (parentOrgId) {
        // Current org is a legal entity, set its parent as orgId and itself as legalEntityId
        methods.reset({
          orgId: parentOrgId,
          legalEntityId: currentOrg,
        })
      } else {
        // Current org is a parent org
        const hasLegalEntities = hasEntities(currentOrg)
        methods.reset({
          orgId: currentOrg,
          // If the org has entities, always default to "all"
          legalEntityId: hasLegalEntities ? ALL_ENTITIES_VALUE : undefined,
        })
      }
    }
  }, [currentOrg, findParentOrg, isLoading, methods, hasEntities])

  return methods
}

export default useOrgManagerFormMethods
